import React, { useState } from 'react';
import { Check, X, BookOpen, Battery, Heart, ShieldCheck, Gift, ChevronLeft, ChevronRight, Brain, Moon, MessageCircle } from 'lucide-react';
import { CountdownTimer } from '../components/CountdownTimer';
import { CTAButton } from '../components/CTAButton';
import { TestimonialCard } from '../components/TestimonialCard';
import { FAQAccordion } from '../components/FAQAccordion';
import { SocialProofPopup } from '../components/SocialProofPopup';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/v6U3H9v6bckAnUTtJ33QQW/Mockup.png';
const BONUS_1_IMAGE = 'https://cdn.magicpatterns.com/uploads/jkbcEbn8utzJ4yZVSaxDQA/Mockup_-_Bono_1.png';
const BONUS_2_IMAGE = 'https://cdn.magicpatterns.com/uploads/kXc3M5wCydP356nM35Gnuh/Mockup_-_Bono_2.png';
const BONUS_3_IMAGE = 'https://cdn.magicpatterns.com/uploads/qR5T7dfWxWPmPYNWYg44a1/Moclup_-_Bono_3.png';
// Empty preview pages as requested
const PREVIEW_PAGES: string[] = ['https://cdn.magicpatterns.com/uploads/m5DR43y2WGgfY1CBmmVxL7/Hoja_1.jpg', 'https://cdn.magicpatterns.com/uploads/mpPocqHw5Hm51ZZJ4n7EN1/Hoja_2.jpg', 'https://cdn.magicpatterns.com/uploads/uzy4LUFiSec43Ch9h21KMt/Hoja_3.jpg', 'https://cdn.magicpatterns.com/uploads/6jxh7BM36nbzzWcvhoPXyS/Hoja_4.jpg', 'https://cdn.magicpatterns.com/uploads/8BzkZGror5mWBux6qU9Phy/Hoja_5.jpg', 'https://cdn.magicpatterns.com/uploads/qs4DZzSe3gSeC1zf1xDPmq/Hoja_6.jpg'];
const SECTION_LINK = '#compra';
export function SalesPage() {
  const [currentPage, setCurrentPage] = useState(0);
  // Only show navigation if there are pages
  const showNavigation = PREVIEW_PAGES.length > 1;
  const nextPage = () => {
    if (!showNavigation) return;
    setCurrentPage(prev => (prev + 1) % PREVIEW_PAGES.length);
  };
  const prevPage = () => {
    if (!showNavigation) return;
    setCurrentPage(prev => (prev - 1 + PREVIEW_PAGES.length) % PREVIEW_PAGES.length);
  };
  return <div className="min-h-screen bg-white font-sans text-gray-900 pb-20" data-id="element-34">
      <CountdownTimer data-id="element-35" />
      <SocialProofPopup data-id="element-36" />

      {/* HERO SECTION */}
      <section className="pt-8 pb-12 px-4 md:pt-12 md:pb-20 bg-gradient-to-b from-white to-gray-50" data-id="element-37">
        <div className="max-w-4xl mx-auto text-center" data-id="element-38">
          {/* Blue pill container for buyer persona text */}
          <div className="inline-flex items-center justify-center bg-blue-100 text-blue-700 px-6 py-2 rounded-full mb-6" data-id="element-39">
            <span className="text-sm md:text-base font-medium uppercase tracking-wide" data-id="element-40">
              🧠 PARA MUJERES QUE VIVEN EN MODO ALERTA CONSTANTE
            </span>
          </div>

          <h1 className="text-[28px] md:text-5xl font-black tracking-tight text-gray-900 mb-8 leading-[1.15]" data-id="element-41">
            Cómo salir del modo alerta y recuperar calma real en tu cuerpo en 30
            días reentrenando tu sistema nervioso
          </h1>

          <div className="mb-6" data-id="element-42">
            <a href={SECTION_LINK} data-id="element-43">
              <img src={PRODUCT_IMAGE} alt="MegaPack De Alerta a Calma" className="w-full max-w-lg mx-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300 cursor-pointer" data-id="element-44" />
            </a>
          </div>

          <p className="text-xl md:text-2xl font-bold text-gray-900 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-45">
            El sistema práctico para mujeres con{' '}
            <span className="bg-yellow-300 px-1" data-id="element-46">mente acelerada</span> que
            quieren dejar de vivir en{' '}
            <span className="bg-yellow-300 px-1" data-id="element-47">alerta constante</span> y
            volver a sentirse seguras en su propio cuerpo.
          </p>

          <CTAButton data-id="element-48">👉 SÍ, QUIERO RECUPERAR MI CALMA</CTAButton>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-49">
        <div className="max-w-3xl mx-auto" data-id="element-50">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-51">
            Si eres "funcional", pero por dentro estás agotada… déjame
            preguntarte algo honesto
          </h2>

          <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-52">
            <p className="font-medium" data-id="element-53">¿Tu mente no se apaga nunca?</p>
            <p data-id="element-54">¿Te acuestas cansada pero tu cabeza sigue activa?</p>
            <p data-id="element-55">¿Le das vueltas a conversaciones durante horas?</p>
            <p data-id="element-56">¿Reaccionas demasiado fuerte en discusiones?</p>
            <p data-id="element-57">¿Te sientes productiva… pero emocionalmente exhausta?</p>

            <p className="text-center font-bold text-2xl mt-8 border-l-4 border-[#dc2626] pl-4 py-2 bg-red-50" data-id="element-58">
              Y no está en tu mente.
              <br data-id="element-59" />
              Está en tu sistema nervioso.
            </p>
          </div>

          <div className="mt-12" data-id="element-60">
            <CTAButton data-id="element-61">QUIERO SALIR DEL MODO ALERTA</CTAButton>
          </div>
        </div>
      </section>

      {/* PAIN POINTS SECTION */}
      <section className="py-16 px-4 bg-gray-900 text-white" data-id="element-62">
        <div className="max-w-4xl mx-auto" data-id="element-63">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-yellow-400" data-id="element-64">
            El problema que casi nadie te explicó sobre la ansiedad
          </h2>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-65">
            <div className="space-y-4" data-id="element-66">
              <p className="text-xl font-medium mb-6" data-id="element-67">
                Si esto te pasa, probablemente te resulte familiar:
              </p>
              <ul className="space-y-4" data-id="element-68">
                {['Vives en hipervigilancia constante', 'Te cuesta relajarte incluso cuando todo está bien', 'Te activas con facilidad ante cualquier tensión', 'Te cuesta dormir profundamente', 'Te sientes en "modo supervivencia" sin razón clara'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-69">
                    <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-70" />
                    <span className="text-lg" data-id="element-71">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-gray-800 p-8 rounded-xl border border-gray-700" data-id="element-72">
              <p className="text-lg mb-6" data-id="element-73">
                Lo peor es esa sensación interna de:
              </p>
              <p className="text-2xl font-bold italic text-gray-300 mb-8" data-id="element-74">
                "Estoy haciendo todo lo posible… pero mi mente no se apaga."
              </p>

              <div className="space-y-2 font-bold text-red-400 text-xl" data-id="element-75">
                <p data-id="element-76">❌ No es que estés pensando demasiado.</p>
                <p data-id="element-77">❌ No es que necesites más fuerza de voluntad.</p>
              </div>

              <p className="mt-6 text-gray-300" data-id="element-78">
                El problema es otro.
                <br data-id="element-79" />
                Tu sistema nervioso quedó atrapado en modo alerta.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-80">
            <p className="text-2xl font-bold text-[#22c55e]" data-id="element-81">
              👉 El Sistema Reset Nervioso™ nace para cortar ese ciclo.
            </p>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-82">
        <div className="max-w-5xl mx-auto" data-id="element-83">
          <div className="text-center mb-16" data-id="element-84">
            <span className="bg-green-100 text-green-800 px-4 py-1 rounded-full font-bold text-sm uppercase tracking-wide" data-id="element-85">
              ✅ LA SOLUCIÓN
            </span>
            <h2 className="text-4xl md:text-5xl font-black mt-6 mb-4" data-id="element-86">
              Sistema Reset Nervioso™
            </h2>
            <p className="text-xl font-bold text-gray-600 max-w-3xl mx-auto" data-id="element-87">
              Un sistema completo de entrenamiento nervioso en 6 fases prácticas
              diseñado para ayudarte a salir del modo alerta y recuperar
              estabilidad emocional real.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-88">
            <div className="bg-green-50 p-8 rounded-2xl border border-green-100" data-id="element-89">
              <h3 className="text-2xl font-bold mb-6" data-id="element-90">
                450+ páginas de entrenamiento emocional práctico:
              </h3>
              <ul className="space-y-4" data-id="element-91">
                {['Sin teoría pesada', 'Sin espiritualidad confusa', 'Sin frases motivacionales vacías', 'Solo herramientas prácticas'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-92">
                    <Check className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-93" />
                    <span className="text-lg font-medium text-gray-800" data-id="element-94">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="flex flex-col justify-center items-center text-center p-8 bg-gray-50 rounded-2xl border border-gray-100" data-id="element-95">
              <div className="space-y-6 text-xl font-bold text-gray-800" data-id="element-96">
                <div className="flex items-center gap-4" data-id="element-97">
                  <span className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm flex-shrink-0" data-id="element-98">
                    1
                  </span>
                  <span className="text-left" data-id="element-99">📂 Abres el material</span>
                </div>
                <div className="flex items-center gap-4" data-id="element-100">
                  <span className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm flex-shrink-0" data-id="element-101">
                    2
                  </span>
                  <span className="text-left" data-id="element-102">
                    👆 Identificas tu patrón de activación
                  </span>
                </div>
                <div className="flex items-center gap-4" data-id="element-103">
                  <span className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm flex-shrink-0" data-id="element-104">
                    3
                  </span>
                  <span className="text-left" data-id="element-105">
                    🧘‍♀️ Aplicás los protocolos para regular tu sistema nervioso
                  </span>
                </div>
              </div>
              <p className="mt-8 text-lg text-gray-600 italic" data-id="element-106">
                Sin luchar contra tu mente. Sin forzarte a "pensar positivo".
                Sin empezar de cero cada semana.
              </p>
            </div>
          </div>

          <CTAButton data-id="element-107">👉 QUIERO EMPEZAR AHORA</CTAButton>
        </div>
      </section>

      {/* MODULES SECTION */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-108">
        <div className="max-w-5xl mx-auto" data-id="element-109">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4" data-id="element-110">
            📘 ¿Qué incluye el Sistema Reset Nervioso™?
          </h2>
          <p className="text-center text-xl font-bold text-gray-600 mb-12" data-id="element-111">
            🔹 6 TOMOS PRINCIPALES: Un entrenamiento progresivo para reentrenar
            tu sistema nervioso y salir del modo alerta.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-112">
            {[{
            icon: '📘',
            title: 'Tomo 1 – Entender para Calmar',
            desc: 'Dejá de culparte y entendé por qué tu cuerpo vive en modo alerta.'
          }, {
            icon: '📘',
            title: 'Tomo 2 – Salir del Modo Alerta',
            desc: 'Empezá a bajar la hipervigilancia y sentir más calma en tu cuerpo en minutos.'
          }, {
            icon: '📘',
            title: 'Tomo 3 – Activar la Calma',
            desc: 'Aprendé a activar calma real en tu cuerpo cuando sientes que todo se acelera.'
          }, {
            icon: '📘',
            title: 'Tomo 4 – Regular Emociones Intensas',
            desc: 'Dejá de reaccionar impulsivamente y aprendé a sostener emociones intensas.'
          }, {
            icon: '📘',
            title: 'Tomo 5 – Construir Resiliencia Diaria',
            desc: 'Construí estabilidad emocional diaria para no volver al modo alerta.'
          }, {
            icon: '📘',
            title: 'Tomo 6 – Calma Sostenida',
            desc: 'Integrá una calma estable que se mantiene incluso en momentos difíciles.'
          }].map((tomo, i) => <a href={SECTION_LINK} key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer" data-id="element-113">
                <div className="text-4xl mb-4" data-id="element-114">{tomo.icon}</div>
                <h3 className="text-xl font-bold mb-2" data-id="element-115">{tomo.title}</h3>
                <p className="text-gray-600" data-id="element-116">{tomo.desc}</p>
              </a>)}
          </div>

          <p className="text-center text-lg font-medium mt-12 text-gray-700" data-id="element-117">
            👉 Todo organizado para que sepas qué hacer y cuándo hacerlo.
          </p>
        </div>
      </section>

      {/* BEFORE VS AFTER */}
      <section className="py-16 px-4 bg-white" data-id="element-118">
        <div className="max-w-4xl mx-auto" data-id="element-119">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-120">
            Antes vs Después
          </h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-121">
            <div className="bg-red-50 p-8 rounded-2xl border border-red-100" data-id="element-122">
              <h3 className="text-xl font-bold text-red-800 mb-6 text-center" data-id="element-123">
                Antes del Sistema Reset Nervioso™
              </h3>
              <ul className="space-y-4" data-id="element-124">
                {['Tu mente no se apaga nunca', 'Vivís en estado de alerta todo el tiempo', 'Reaccionás demasiado rápido ante tensiones', 'Te sentís agotada por dentro', 'Te cuesta dormir profundamente', 'Sentís inseguridad emocional constante'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-125">
                    <X className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-126" />
                    <span className="text-gray-700" data-id="element-127">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-green-50 p-8 rounded-2xl border border-green-100 shadow-lg transform md:-translate-y-4" data-id="element-128">
              <h3 className="text-xl font-bold text-green-800 mb-6 text-center" data-id="element-129">
                Después del Sistema Reset Nervioso™
              </h3>
              <ul className="space-y-4" data-id="element-130">
                {['Tu cuerpo puede relajarse de verdad', 'Respondés con calma en lugar de reaccionar', 'Tu mente se aquieta con facilidad', 'Dormís más profundo', 'Manejás conflictos sin explotar', 'Sentís seguridad interna'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-131">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-132" />
                    <span className="font-bold text-gray-800" data-id="element-133">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-16 px-4 bg-[#0f172a] text-white" data-id="element-134">
        <div className="max-w-5xl mx-auto" data-id="element-135">
          <div className="text-center mb-12" data-id="element-136">
            <span className="bg-yellow-400 text-black px-4 py-1 rounded-full font-bold text-sm uppercase tracking-wide animate-pulse" data-id="element-137">
              🎁 REGALOS ESPECIALES
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mt-6 mb-4" data-id="element-138">
              Bonos diseñados para que la calma se vuelva estable
            </h2>
            <p className="text-xl text-yellow-400 font-bold" data-id="element-139">
              ¡GRATIS POR HOY!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-140">
            {/* Bonus 1 */}
            <a href={SECTION_LINK} className="bg-[#1e293b] rounded-xl p-6 border border-[#334155] flex flex-col items-center text-center hover:border-yellow-400 transition-colors cursor-pointer" data-id="element-141">
              <div className="w-full aspect-square max-w-[270px] mb-6 flex items-center justify-center rounded-lg bg-[#1e293b]" data-id="element-142">
                <img src={BONUS_1_IMAGE} alt="Bono 1 - Sueño en Calma" className="w-full h-full object-contain drop-shadow-xl" data-id="element-143" />
              </div>
              <p className="text-gray-400 text-sm mb-2" data-id="element-144">
                Valor:{' '}
                <span className="font-bold line-through decoration-red-400" data-id="element-145">
                  $27.000 ARS
                </span>
              </p>
              <span className="bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-146">
                GRATIS HOY
              </span>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-147">
                🌙 Bono #1 — Sueño en Calma
              </h3>
              <p className="text-gray-300 text-sm" data-id="element-148">
                Protocolo nocturno para que tu mente se apague y puedas dormir
                profundamente.
              </p>
            </a>

            {/* Bonus 2 */}
            <a href={SECTION_LINK} className="bg-[#1e293b] rounded-xl p-6 border border-[#334155] flex flex-col items-center text-center hover:border-yellow-400 transition-colors cursor-pointer" data-id="element-149">
              <div className="w-full aspect-square max-w-[270px] mb-6 flex items-center justify-center rounded-lg bg-[#1e293b]" data-id="element-150">
                <img src={BONUS_2_IMAGE} alt="Bono 2 - Cortar el Sobrepensamiento" className="w-full h-full object-contain drop-shadow-xl" data-id="element-151" />
              </div>
              <p className="text-gray-400 text-sm mb-2" data-id="element-152">
                Valor:{' '}
                <span className="font-bold line-through decoration-red-400" data-id="element-153">
                  $21.000 ARS
                </span>
              </p>
              <span className="bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-154">
                GRATIS HOY
              </span>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-155">
                🧠 Bono #2 — Cortar el Sobrepensamiento
              </h3>
              <p className="text-gray-300 text-sm" data-id="element-156">
                Plan práctico de 7 días para cortar el sobrepensamiento y
                recuperar claridad mental.
              </p>
            </a>

            {/* Bonus 3 */}
            <a href={SECTION_LINK} className="bg-[#1e293b] rounded-xl p-6 border border-[#334155] flex flex-col items-center text-center hover:border-yellow-400 transition-colors cursor-pointer" data-id="element-157">
              <div className="w-full aspect-square max-w-[270px] mb-6 flex items-center justify-center rounded-lg bg-[#1e293b]" data-id="element-158">
                <img src={BONUS_3_IMAGE} alt="Bono 3 - Conversaciones sin Ansiedad" className="w-full h-full object-contain drop-shadow-xl" data-id="element-159" />
              </div>
              <p className="text-gray-400 text-sm mb-2" data-id="element-160">
                Valor:{' '}
                <span className="font-bold line-through decoration-red-400" data-id="element-161">
                  $17.000 ARS
                </span>
              </p>
              <span className="bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-162">
                GRATIS HOY
              </span>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-163">
                💬 Bono #3 — Conversaciones sin Ansiedad
              </h3>
              <p className="text-gray-300 text-sm" data-id="element-164">
                Aprendé a hablar con calma incluso en conversaciones difíciles.
              </p>
            </a>
          </div>

          <div className="mt-12 text-center space-y-2 text-lg font-medium" data-id="element-165">
            <p data-id="element-166">👉 Los tomos te enseñan a regular tu sistema nervioso.</p>
            <p data-id="element-167">👉 Los bonos evitan que vuelvas al modo alerta.</p>
            <p className="text-yellow-400 font-bold text-xl mt-4" data-id="element-168">
              Eso es lo que hace que el cambio se vuelva sostenible.
            </p>
          </div>

          <div className="mt-12" data-id="element-169">
            <CTAButton data-id="element-170">QUIERO MIS BONOS AHORA</CTAButton>
          </div>
        </div>
      </section>

      {/* OFFER STACK SECTION */}
      <section id="compra" className="py-16 px-4 bg-gradient-to-b from-gray-900 to-gray-800" data-id="element-171">
        <div className="max-w-4xl mx-auto" data-id="element-172">
          {/* Big Title */}
          <div className="text-center mb-10" data-id="element-173">
            <span className="bg-red-500 text-white px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wide animate-pulse inline-block mb-4" data-id="element-174">
              ⏰ OFERTA ESPECIAL
            </span>
            <h2 className="text-4xl md:text-6xl font-black text-white uppercase leading-tight" data-id="element-175">
              Oferta por
              <br data-id="element-176" />
              <span className="text-yellow-400" data-id="element-177">Tiempo Limitado</span>
            </h2>
          </div>

          {/* Offer Box */}
          <div className="bg-white rounded-3xl p-6 md:p-12 shadow-2xl" data-id="element-178">
            <h3 className="text-2xl md:text-3xl font-black text-center mb-8 text-gray-900" data-id="element-179">
              📦 Lo que recibes hoy
            </h3>

            <div className="space-y-4 mb-10" data-id="element-180">
              {[{
              name: '6 tomos completos',
              price: '$49.000'
            }, {
              name: 'Bono "Sueño en Calma"',
              price: '$27.000'
            }, {
              name: 'Bono "Cortar el Sobrepensamiento"',
              price: '$21.000'
            }, {
              name: 'Bono "Conversaciones sin Ansiedad"',
              price: '$17.000'
            }].map((item, i) => <div key={i} className="flex items-center justify-between gap-3 text-lg md:text-xl border-b border-gray-200 pb-3 last:border-0" data-id="element-181">
                  <div className="flex items-center gap-3" data-id="element-182">
                    <Check className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-183" />
                    <span className="font-bold text-gray-800" data-id="element-184">{item.name}</span>
                  </div>
                  <span className="text-gray-500 font-medium whitespace-nowrap" data-id="element-185">
                    → {item.price}
                  </span>
                </div>)}
            </div>

            <div className="text-center mb-8" data-id="element-186">
              <p className="text-gray-500 text-xl mb-2" data-id="element-187">
                Valor total del sistema:{' '}
                <span className="line-through decoration-red-500 decoration-2 font-bold" data-id="element-188">
                  $114.000 ARS
                </span>
              </p>
              <p className="text-5xl md:text-7xl font-black text-[#22c55e]" data-id="element-189">
                🔥 $19.990 ARS
              </p>
              <p className="text-gray-800 mt-2 font-bold" data-id="element-190">
                Un solo pago. Sin suscripciones.
              </p>
            </div>

            <div className="flex flex-col items-center justify-center w-full" data-id="element-191">
              <a href="https://checkout.tumundoglobal.online/cart/45134986313919:1" className="relative group flex items-center justify-center gap-2 w-full md:max-w-2xl px-6 py-5 md:py-6 bg-[#22c55e] hover:bg-[#16a34a] text-white text-center rounded-xl shadow-xl hover:shadow-2xl transform transition-all duration-200 hover:-translate-y-1 border-b-4 border-[#15803d] active:border-b-0 active:translate-y-1 text-xl md:text-2xl font-bold uppercase tracking-wide" data-id="element-192">
                👉 ACCEDER AL SISTEMA RESET NERVIOSO
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 md:w-7 md:h-7" data-id="element-193">
                  <path d="M5 12h14" data-id="element-194" />
                  <path d="m12 5 7 7-7 7" data-id="element-195" />
                </svg>
              </a>
              <p className="mt-4 text-sm text-gray-500 flex items-center gap-3 flex-wrap justify-center" data-id="element-196">
                <span data-id="element-197">🔒 Pago seguro</span>
                <span data-id="element-198">•</span>
                <span data-id="element-199">⚡ Acceso inmediato</span>
                <span data-id="element-200">•</span>
                <span data-id="element-201">🛡 Garantía de 7 días</span>
              </p>
            </div>
          </div>

          {/* GUARANTEE - Outside the offer box */}
          <div className="mt-10 flex flex-col md:flex-row items-center gap-6 bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20" data-id="element-202">
            <ShieldCheck className="w-16 h-16 text-yellow-400 flex-shrink-0" data-id="element-203" />
            <div className="text-center md:text-left" data-id="element-204">
              <h3 className="text-xl font-bold mb-2 text-white" data-id="element-205">
                Garantía de satisfacción
              </h3>
              <p className="text-gray-300" data-id="element-206">
                Si en los primeros 7 días sentís que el sistema no es para vos,
                podés solicitar un reembolso completo. Sin preguntas. Sin
                compromisos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PREVIEW PAGES CAROUSEL - Structure kept but content empty */}
      <section className="py-16 px-4 bg-white" data-id="element-207">
        <div className="max-w-4xl mx-auto" data-id="element-208">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4" data-id="element-209">
            📖 Revisá algunas páginas
          </h2>
          <p className="text-center text-gray-600 mb-10" data-id="element-210">
            Mira una muestra del contenido práctico que recibirás:
          </p>

          {/* Carousel */}
          <div className="relative" data-id="element-211">
            <div className="overflow-hidden rounded-2xl shadow-lg border border-gray-200 bg-white" data-id="element-212">
              <img src={PREVIEW_PAGES[currentPage]} alt={`Página de muestra ${currentPage + 1}`} className="w-full h-auto" data-id="element-213" />
            </div>

            {/* Left Arrow */}
            <button onClick={prevPage} className="absolute left-2 md:-left-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors z-10" aria-label="Página anterior" data-id="element-214">
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-700" data-id="element-215" />
            </button>

            {/* Right Arrow */}
            <button onClick={nextPage} className="absolute right-2 md:-right-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors z-10" aria-label="Página siguiente" data-id="element-216">
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-700" data-id="element-217" />
            </button>

            {/* Dots indicator */}
            <div className="flex items-center justify-center gap-2 mt-6" data-id="element-218">
              {PREVIEW_PAGES.map((_, i) => <button key={i} onClick={() => setCurrentPage(i)} className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${i === currentPage ? 'bg-gray-800 w-6' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Ir a página ${i + 1}`} data-id="element-219" />)}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-220">
        <div className="max-w-6xl mx-auto" data-id="element-221">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-222">
            Lo que están experimentando mujeres que aplicaron el sistema
          </h2>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-223">
            <TestimonialCard name="Sofía" age={34} location="Buenos Aires" quote={<>
                  Sentía que vivía corriendo incluso cuando estaba sentada. Con
                  los ejercicios del Tomo 2 entendí que mi cuerpo estaba en modo
                  alerta constante.{' '}
                  <span className="font-bold bg-green-100 text-green-800 px-1 not-italic" data-id="element-225">
                    Empecé a bajar esa activación y la diferencia es enorme.
                  </span>
                </>} data-id="element-224" />
            <TestimonialCard name="Mariana" age={41} location="Córdoba" quote={<>
                  Llevo años en terapia, pero en el momento en que iba a
                  explotar no sabía qué hacer. Este sistema me dio herramientas
                  concretas para esos momentos.{' '}
                  <span className="font-bold bg-green-100 text-green-800 px-1 not-italic" data-id="element-227">
                    Ahora sé cómo regularme cuando empiezo a activarme.
                  </span>
                </>} data-id="element-226" />
            <TestimonialCard name="Lucía" age={29} location="Rosario" quote={<>
                  Pasaba horas en la cama pensando en lo que dije o debería
                  haber dicho. El bono de sobrepensamiento me ayudó a cortar ese
                  bucle mental.{' '}
                  <span className="font-bold bg-green-100 text-green-800 px-1 not-italic" data-id="element-229">
                    Desde entonces duermo mucho mejor.
                  </span>
                </>} data-id="element-228" />
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-6" data-id="element-230">
            <TestimonialCard name="Carolina" age={38} location="Mendoza" quote={<>
                  Pensé que tenía que "pensar positivo" para sentirme mejor.
                  Este material me hizo entender qué pasa en el cuerpo cuando
                  estás en alerta.{' '}
                  <span className="font-bold bg-green-100 text-green-800 px-1 not-italic" data-id="element-232">
                    Hoy sé cómo calmar mi sistema nervioso.
                  </span>
                </>} data-id="element-231" />
            <TestimonialCard name="Valentina" age={45} location="La Plata" quote={<>
                  Reaccionaba muy rápido con mis hijos cuando estaba estresada.{' '}
                  <span className="font-bold bg-green-100 text-green-800 px-1 not-italic" data-id="element-234">
                    Aprendí a reconocer cuándo mi cuerpo se activa y a regularme
                    antes de reaccionar.
                  </span>{' '}
                  Mis hijos notaron el cambio.
                </>} data-id="element-233" />
            <TestimonialCard name="Ana" age={32} location="Santa Fe" quote={<>
                  Siempre pensé que simplemente era una persona ansiosa. Con el
                  sistema entendí que era mi sistema nervioso pidiendo ayuda.{' '}
                  <span className="font-bold bg-green-100 text-green-800 px-1 not-italic" data-id="element-236">
                    Eso cambió completamente cómo manejo mis emociones.
                  </span>
                </>} data-id="element-235" />
          </div>

          <p className="text-center text-xl font-bold text-gray-800 mt-12" data-id="element-237">
            La regulación emocional no es un talento. Es una habilidad que se
            entrena.
          </p>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-238">
        <div className="max-w-3xl mx-auto" data-id="element-239">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-240">
            Preguntas frecuentes
          </h2>
          <FAQAccordion data-id="element-241" />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-4 bg-gray-900 text-white text-center" data-id="element-242">
        <div className="max-w-3xl mx-auto" data-id="element-243">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-244">
            🧠 Recuperar la calma es más simple de lo que crees
          </h2>
          <div className="text-xl text-gray-300 mb-10 leading-relaxed space-y-2" data-id="element-245">
            <p className="font-bold" data-id="element-246">No necesitás pensar más.</p>
            <p className="font-bold" data-id="element-247">No necesitás ser más fuerte.</p>
            <p className="font-bold" data-id="element-248">Necesitás entrenar tu sistema nervioso.</p>
          </div>

          <a href={SECTION_LINK} className="inline-flex items-center justify-center gap-2 px-12 py-5 md:py-6 bg-[#22c55e] hover:bg-[#16a34a] text-white text-center rounded-xl shadow-xl hover:shadow-2xl transform transition-all duration-200 hover:-translate-y-1 border-b-4 border-[#15803d] active:border-b-0 active:translate-y-1 text-lg md:text-2xl font-bold uppercase tracking-wide" data-id="element-249">
            QUIERO EMPEZAR MI RESET NERVIOSO
          </a>

          <p className="mt-8 text-yellow-400 font-bold text-lg tracking-wide" data-id="element-250">
            LA CALMA NO SE PIENSA. SE ENTRENA.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 bg-black text-gray-500 text-center text-sm" data-id="element-251">
        <p data-id="element-252">© 2024 De Alerta a Calma. Todos los derechos reservados.</p>
        <p className="mt-2" data-id="element-253">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
        </p>
      </footer>
    </div>;
}